import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Slide } from '@mui/material';

import http, { setUnauthorizedHandler } from '../api/http';
import AppSettings from '../config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(
        !!localStorage.getItem('token')
    );
    const [activeUser, setActiveUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('activeUser')) || null;
        } catch (e) {
            return null;
        }
    });
    const [pageTitle, setPageTile] = useState('');
    const [notificationsCount, setNotificationsCount] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [time, setTime] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const [activeSessionCounter, setActiveSessionCounter] = useState('NO');
    const [visitSession, setVisitSession] = useState(null);
    const lastUnreadRef = useRef(0);
    const lastSessionPhaseRef = useRef('');

    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { enableNotifications } = AppSettings;

    useEffect(() => {
        if (location.pathname.startsWith('/patients/') && location.pathname !== '/patients') {
            setPageTile('Patient File');
            return;
        }
        if (location.pathname.includes('/print')) {
            setPageTile('Prescription');
            return;
        }
        switch (location.pathname) {
            case '/dashboard':
                setPageTile('Dashboard');
                break;
            case '/patients':
                setPageTile('Patient Management');
                break;
            case '/visit-history':
                setPageTile('Appointments');
                break;
            case '/follow-ups':
                setPageTile('Follow-ups');
                break;
            case '/issues':
                setPageTile('Patient Issues');
                break;
            case '/admin':
                setPageTile('Administration');
                break;
            case '/visit-requests':
                setPageTile('Visit requests');
                break;
            case '/availability':
                setPageTile('Visit hours');
                break;
            case '/my-visits':
                setPageTile('My visits');
                break;
            case '/verify':
                setPageTile('Verify contact');
                break;
            case '/notices':
                setPageTile('Clinic notices');
                break;
            default:
                setPageTile('');
                break;
        }
    }, [location.pathname]);

    useEffect(() => {
        const formatTime = () => {
            const minutes = Math.floor(time / 60000);
            const seconds = Math.floor((time % 60000) / 1000);
            const milliseconds = ((time % 1000) / 10).toFixed(0);

            if (isRunning) {
                setActiveSessionCounter(
                    `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}.${milliseconds < 10 ? '0' + milliseconds : milliseconds}`
                );
            } else {
                setActiveSessionCounter('NO');
            }
        };

        let interval;
        if (isRunning) {
            interval = setInterval(() => {
                setTime((prevTime) => prevTime + 10);
            }, 10);
        }

        formatTime();
        return () => clearInterval(interval);
    }, [isRunning, time]);

    const pathRef = useRef(location.pathname);
    pathRef.current = location.pathname;

    const clearSession = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('activeUser');
        setIsAuthenticated(false);
        setActiveUser(null);
    }, []);

    const expireSession = useCallback((message) => {
        const publicAuthPath = ['/login', '/forget-password', '/reset-password']
            .includes(pathRef.current);
        const hadSession = !!(
            localStorage.getItem('token')
            || localStorage.getItem('activeUser')
        );

        clearSession();

        if (!publicAuthPath) {
            navigate('/login', { replace: true });
        }

        if (hadSession && message) {
            enqueueSnackbar(message, {
                variant: 'warning',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
                TransitionComponent: Slide,
            });
        }
    }, [clearSession, enqueueSnackbar, navigate]);

    useEffect(() => {
        setUnauthorizedHandler(() => {
            expireSession('Your session has expired. Please sign in again.');
        });
        return () => setUnauthorizedHandler(null);
    }, [expireSession]);

    useEffect(() => {
        const restoreSession = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }
            try {
                const response = await http.get('/auth');
                if (response.data && response.data.userID) {
                    setActiveUser(response.data);
                    localStorage.setItem('activeUser', JSON.stringify(response.data));
                    setIsAuthenticated(true);
                    return;
                }
                expireSession('Your session has expired. Please sign in again.');
            } catch (error) {
                if (error.response?.data?.code !== 'SESSION_EXPIRED') {
                    expireSession('Your session has expired. Please sign in again.');
                }
            }
        };

        restoreSession();
    }, [expireSession]);

    const promptStaff = useCallback((title, body) => {
        enqueueSnackbar(body || title, {
            variant: 'info',
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            TransitionComponent: Slide,
        });
        if (typeof window !== 'undefined' && window.Notification) {
            if (window.Notification.permission === 'granted') {
                new window.Notification(title, { body, icon: '/favicon.ico' });
            } else if (window.Notification.permission === 'default') {
                window.Notification.requestPermission();
            }
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        const getUserNotificationsAfterLogin = async () => {
            try {
                const response = await http.get('/notification', { params: { unread: 1, perPage: 1 } });
                const unread = Number(response.data?.unreadCount || 0);
                if (unread > lastUnreadRef.current && lastUnreadRef.current > 0) {
                    const latest = response.data?.data?.[0];
                    promptStaff(latest?.title || 'New clinic alert', latest?.body || 'Something is coming.');
                }
                lastUnreadRef.current = unread;
                setNotificationsCount(unread);
            } catch (error) {
                return null;
            }
        };

        let interval;
        if (isAuthenticated && enableNotifications) {
            getUserNotificationsAfterLogin();
            interval = setInterval(getUserNotificationsAfterLogin, 20000);
        }

        return () => clearInterval(interval);
    }, [isAuthenticated, enableNotifications, promptStaff]);

    useEffect(() => {
        if (!isAuthenticated) {
            setVisitSession(null);
            lastSessionPhaseRef.current = '';
            lastUnreadRef.current = 0;
            return undefined;
        }
        const loadSession = async () => {
            try {
                const response = await http.get('/visit-request/session');
                const current = response.data?.current || null;
                setVisitSession(current);
                const key = current ? `${current.visitRequestID}:${current.phase}` : '';
                const previous = lastSessionPhaseRef.current;
                lastSessionPhaseRef.current = key;
                if (current && previous && key !== previous) {
                    if (current.phase === 'upcoming' && current.startsInMs <= 15 * 60 * 1000) {
                        promptStaff('Visit coming up', `${current.patientName} starts soon.`);
                    }
                    if (current.phase === 'ongoing') {
                        promptStaff('Visit started', `${current.patientName} is with you now.`);
                    }
                    if (current.phase === 'ending') {
                        promptStaff('Visit ending soon', `${current.patientName}'s slot is about to end.`);
                    }
                }
            } catch (error) {
                return null;
            }
        };
        loadSession();
        const interval = setInterval(loadSession, 15000);
        return () => clearInterval(interval);
    }, [isAuthenticated, promptStaff]);

    const handleAppointmentSessionCounter = () => {
        setIsRunning(!isRunning);
    };

    const refreshActiveUser = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            return null;
        }
        try {
            const response = await http.get('/auth');
            if (response.data && response.data.userID) {
                setActiveUser(response.data);
                localStorage.setItem('activeUser', JSON.stringify(response.data));
                setIsAuthenticated(true);
                return response.data;
            }
        } catch (error) {
            return null;
        }
        return null;
    }, []);

    const registerPatient = async (values) => {
        try {
            const response = await http.post('/auth/register-patient', values);
            if (response.data.error) {
                return notifyToUser('error', response.data.message);
            }
            const user = response.data.user || response.data;
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('activeUser', JSON.stringify(user));
            setActiveUser(user);
            setIsAuthenticated(true);
            return navigate(user.isContactVerified === false ? '/verify' : '/my-visits');
        } catch (error) {
            notifyToUser('error', error.response?.data?.message || 'Unable to create the account');
            throw error;
        }
    };

    const login = async (username, password) => {
        try {
            const response = await http.post('/auth/login', {
                username,
                password,
            });

            if (response.data.error) {
                return notifyToUser('error', response.data.message);
            }

            const user = response.data.user || response.data;
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('activeUser', JSON.stringify(user));
            setActiveUser(user);
            setIsAuthenticated(true);
            if (user.role === 'patient' && user.isContactVerified === false) {
                return navigate('/verify');
            }
            return navigate(user.role === 'patient' ? '/my-visits' : '/dashboard');
        } catch (error) {
            if (error.response) {
                return notifyToUser('error', error.response.data?.message || 'Invalid username or password');
            }
            return notifyToUser('error', 'Network error');
        }
    };

    const logout = async () => {
        try {
            await http.get('/auth/logout');
        } catch (error) {
            // token may already be invalid
        }
        clearSession();
        navigate('/login', { replace: true });
    };

    const drawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const drawerNavigation = (path) => {
        navigate(path);
        drawerToggle();
    };

    const notifyToUser = useCallback((variant, message) => {
        enqueueSnackbar(message, {
            variant: variant,
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
            TransitionComponent: Slide,
        });
    }, [enqueueSnackbar]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                activeUser,
                login,
                registerPatient,
                logout,
                refreshActiveUser,
                pageTitle,
                notificationsCount,
                drawerOpen,
                drawerToggle,
                drawerWidth: 240,
                drawerNavigation,
                handleAppointmentSessionCounter,
                activeSessionCounter,
                visitSession,
                notifyToUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
